import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import { GatsbyImage } from 'gatsby-plugin-image';
import { renderRichText } from 'gatsby-source-contentful/rich-text';
import get from 'lodash.get';
import merge from 'lodash.merge';
import PropTypes from 'prop-types';
import React, { useEffect, useRef } from 'react';
import { Client, HydrationProvider } from 'react-hydration-provider';
import ReactPlayer from 'react-player';
import Gist from 'super-react-gist';

import Accordion from '../../Generic/Accordion';
import ContentfulImage from '../ContentfulImage';
import IframeModal from '../IframeModal';
import { ArrowLink, PrimaryLink } from '../Links';
import SmartLink from '../SmartLink';
import * as inlines from './Inlines';
import * as styles from './RichTextArticle.module.scss';
import RichTextConverter from './RichTextConverter';
import YouTubeVideo from './YouTubeVideo';

const RichTextArticle = ({ copy, optionsOverride, className = '' }) => {
  const articleRef = useRef(null);

  let tableHeaders = [];
  let tableHeadersBackup = [];

  useEffect(() => {
    let tableCell = document.querySelectorAll('.rich-text-table-cell');
    tableCell.forEach((element) => {
      if (tableHeaders.length !== 0) {
        const label = tableHeaders.shift();
        if (label) {
          element.setAttribute('data-label', label);
        }
      } else {
        tableHeaders.push(...tableHeadersBackup);
        const label = tableHeaders.shift();
        if (label) {
          element.setAttribute('data-label', label);
        }
      }
    });
  });

  let sections = 0;
  const defaultOptions = {
    renderText: inlines.text,
    renderNode: {
      [INLINES.HYPERLINK]: inlines.hyperlink,
      [INLINES.ENTRY_HYPERLINK]: inlines.entryHyperlink,
      [BLOCKS.HEADING_1]: (node, children) => <h1 className="article-heading">{children}</h1>,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const { file, title, description, gatsbyImageData } = get(node, 'data.target', {});

        return (
          <figure className={styles.figure} key={file?.url}>
            {gatsbyImageData && (
              <GatsbyImage
                loading={'lazy'}
                data-ot-ignore
                className={styles.embeddedAsset}
                image={gatsbyImageData}
                alt={title}
              />
            )}
            {!gatsbyImageData && <ContentfulImage lazy={true} imageFragment={node.data.target} />}
            {description && <figcaption>{description}</figcaption>}
          </figure>
        );
      },
      [BLOCKS.TABLE]: (node, children) => {
        const headerRows = [];
        const bodyRows = [];

        React.Children.forEach(children, (child) => {
          const isHeaderRow = child.props.children.some((cell) => cell.type === 'th');
          if (isHeaderRow) {
            headerRows.push(child);
          } else {
            bodyRows.push(child);
          }
        });

        return (
          <table role="table" className="rich-text-table">
            {headerRows.length > 0 && <thead>{headerRows}</thead>}
            <tbody>{bodyRows}</tbody>
          </table>
        );
      },
      [BLOCKS.TABLE_ROW]: (node, children) => {
        node.content.forEach((element) => {
          if (element.nodeType === 'table-header-cell') {
            tableHeaders.push(element.content[0].content[0].value);
            tableHeadersBackup.push(element.content[0].content[0].value);
          }
        });
        return (
          <tr role="row" className="rich-text-row">
            {children}
          </tr>
        );
      },
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <th className="rich-text-columnheader" role="columnheader">
          {children}
        </th>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => (
        <td role="cell" className="rich-text-table-cell">
          {children}
        </td>
      ),
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const type = get(node, 'data.target.internal.type');
        // Anchor
        if (type === 'ContentfulEmbedJumpLink') {
          let id = get(node, 'data.target.sectionId');
          sections++;
          return <div data-section-num={sections} id={id} key={id} />;
        }

        if (type === 'ContentfulEmbedGithubGist') {
          // github gists
          const url = node?.data?.target?.gistUrl;
          if (!url || typeof window === 'undefined') return;
          const isJS = url.indexOf('.js') > -1;
          if (isJS) {
            return (
              <div className={styles.gistWrap} key={node?.data?.target?.contentful_id}>
                <Gist url={url.split('.js')?.[0]} />
              </div>
            );
          }
          const id = url.split('#')?.[0];
          if (!id) return;
          return (
            <div className={styles.gistWrap} key={id}>
              <Gist url={id} />
            </div>
          );
        }
        if (type === 'ContentfulEmbedVideo') {
          // wistia embeds
          const videoUrl = node.data.target.videoUrl;
          if (videoUrl?.indexOf('wistia') > -1) {
            //return <AutoPauseVideo classes={[styles.reactPlayer]} controls={true} src={videoUrl} url={videoUrl} />;
            return (
              <div className={styles.playerWrapper} key={videoUrl}>
                <HydrationProvider>
                  <Client>
                    <ReactPlayer
                      className={styles.reactPlayer}
                      controls={true}
                      width="100%"
                      height="100%"
                      url={videoUrl}
                    />
                  </Client>
                </HydrationProvider>
              </div>
            );
          }

          return <YouTubeVideo videoUrl={videoUrl} />;
        }
        // embedded images for alignment
        if (type === 'ContentfulEmbedInlineImage') {
          const imageFile = get(node, 'data.target.image');
          const caption = get(node, 'data.target.caption');
          const url = get(node, 'data.target.url');
          const displaySize = get(node, 'data.target.displaySize');
          if (!imageFile) {
            return;
          }
          const side = get(node, 'data.target.alignImage');
          let align = '';
          switch (side) {
            case 'Left':
              align = styles.left;
              break;
            case 'Right':
              align = styles.right;
              break;
            case 'Centered No Padding':
              align = styles.centerNoPad;
              break;
            default:
              align = styles.fullWidth;
          }

          let displaySizeStyle = '';
          if (displaySize === 'Small') {
            displaySizeStyle = styles.displaySizeSmall;
          }

          if (!imageFile.fullWidth) {
            let img = <ContentfulImage lazy={true} imageFragment={imageFile} />;
            if (url) {
              img = <SmartLink to={url}>{img}</SmartLink>;
            }

            return (
              <div className={[styles.fig, align, displaySizeStyle].join(' ')} key={imageFile.file.url}>
                {img}
                {caption && <figcaption>{caption}</figcaption>}
              </div>
            );
          }

          let img = <ContentfulImage className={displaySizeStyle} lazy={true} imageFragment={imageFile} />;
          if (url) {
            img = <SmartLink to={url}>{img}</SmartLink>;
          }
          return (
            <div className={[styles.fig, align, displaySizeStyle].join(' ')} key={imageFile.file.url}>
              <div className={url ? styles.linkedImage : ''}>{img}</div>
              {caption && <figcaption>{caption}</figcaption>}
            </div>
          );
        }

        // embedded callout section
        if (type === 'ContentfulSectionCallout') {
          const body = node.data?.target?.sectionContent || '';
          const {
            style,
            borderStyle,
            borderColor,
            mainImage,
            ctaLabel,
            ctaUrl,
            backgroundImage,
            backgroundOverlayColor,
          } = node?.data?.target;
          const isSurround = borderStyle === 'surround';

          if (style === 'Half and Half') {
            const bgColor = backgroundOverlayColor === 'Pink' ? styles.bgPink : styles.bgPink;
            return (
              <div className={[styles.calloutHalf, bgColor].join(' ')} key={ctaUrl}>
                <div>
                  <RichTextConverter copy={body} />
                </div>
                <div>
                  <PrimaryLink label={ctaLabel} url={ctaUrl} isButton outlined />
                </div>
              </div>
            );
          }

          return (
            <div
              className={[
                isSurround ? styles.calloutSurround : styles.calloutLeft,
                isSurround ? styles[borderColor || 'defaultBorder'] : '',
              ].join(' ')}
              key={ctaUrl}
            >
              {isSurround && backgroundImage && (
                <ContentfulImage imageFragment={backgroundImage} lazy={true} className={styles.surroundBackground} />
              )}
              {mainImage && <ContentfulImage className={styles.calloutImage} imageFragment={mainImage} lazy={true} />}
              <div className={styles.calloutCopy}>{body && body.raw && renderRichText(body, options)}</div>
              {ctaLabel &&
                ctaUrl &&
                (backgroundImage ? (
                  <PrimaryLink isButton url={ctaUrl} label={ctaLabel} className={styles.ctaButton} />
                ) : (
                  <ArrowLink to={ctaUrl} label={ctaLabel} hoverAnimation />
                ))}
            </div>
          );
        }

        if (type === 'ContentfulEmbedLink') {
          const label = get(node, 'data.target.linkText');
          const url = get(node, 'data.target.url');
          if (url.startsWith('https://app.getreprise.com/')) {
            const content = {
              contentType: {
                viewLabel: label,
              },
              ctaExternalLink: url,
            };
            return <IframeModal content={content} key={url} />;
          }
        }

        if (type === 'ContentfulSectionGroupedItems') {
          const section = node.data.target;
          if (section.type === 'Accordion') {
            return (
              <div key={section.contentful_id} data-analytics-section={'accordion'}>
                {section.heading && <h2>{section.heading}</h2>}
                {section.sectionCopy && <RichTextConverter copy={section.sectionCopy} />}
                <Accordion items={section.content} defaultCollapsed={true} />
              </div>
            );
          }
        }
      },
    },
  };

  const options = merge({}, defaultOptions, optionsOverride);
  return (
    <article data-analytics-section={'article'} ref={articleRef} className={[styles.postArticle, className].join(' ')}>
      {copy?.raw && renderRichText(copy, options)}
    </article>
  );
};

RichTextArticle.propTypes = {
  /** The raw json data **/
  copy: PropTypes.object.isRequired,
  optionsOverride: PropTypes.object,
};

export default RichTextArticle;
